// MainHomeworkMetadataContext.tsx
import React, { createContext, useContext, useMemo } from 'react';
import { MasterSkill, Skill, Difficulty, Book, Topic } from '@shared/types';
import { useGetMetadata } from '@shared/data-access';
import { Loading } from '../components/Loading';

interface HomeworkMetadataResponse {
  books: Book[];
  difficulties: Difficulty[];
  masterSkills: MasterSkill[];
  skills: Skill[];
  topics: Topic[];
  dispatcher: {
    getDifficultyName: (id: Difficulty['id']) => string;
    getSkill: (id: Skill['id']) => Skill | undefined;
    getSkillNames: (id: Skill['id']) => { masterSkill: string; skill: string };
    getTopicName: (id: Topic['id']) => string; // Fixed type mapping to Topic['id']
    getAllTopics: (value: string | string[]) => string[];
    getBookName: (id: Book['id']) => string;
  };
}

interface HomeworkMetadataContextValue extends HomeworkMetadataResponse {}

const HomeworkMetadataContext = createContext<HomeworkMetadataContextValue | undefined>(undefined);

export const MainHomeworkMetadataProvider = ({ children }: { children: React.ReactNode }) => {
  const { data, isLoading: loading } = useGetMetadata();

  // Create memoized lookup records once data is available
  const derivedMaps = useMemo(() => {
    if (!data) return null;

    const booksMap: Record<string, string> = {};
    const difficultiesMap: Record<string, string> = {};
    const masterSkillsMap: Record<string, string> = {};
    const topicsMap: Record<string, string> = {};
    const skillsMap: Record<string, Skill> = {};

    data.books?.forEach(b => {
      booksMap[b.id] = b.name;
    });
    data.difficulties?.forEach(d => {
      difficultiesMap[d.id] = d.name;
    });
    data.masterSkills?.forEach(m => {
      masterSkillsMap[m.id] = m.name;
    });
    data.topics?.forEach(t => {
      topicsMap[t.id] = t.name;
    });
    data.skills?.forEach(s => {
      skillsMap[s.id] = s;
    });

    return { booksMap, difficultiesMap, masterSkillsMap, topicsMap, skillsMap };
  }, [data]);

  if (loading) return <Loading />;
  if (!data || !derivedMaps) return null;

  const { booksMap, difficultiesMap, masterSkillsMap, topicsMap, skillsMap } = derivedMaps;

  const getSkillNames = (id: Skill['id']): { masterSkill: string; skill: string } => {
    const masterSkill = masterSkillsMap[id];
    if (masterSkill) {
      return { masterSkill, skill: '' };
    }

    const skill = skillsMap[id];
    if (!skill) return { masterSkill: '', skill: '' };

    const masterSkillName = masterSkillsMap[skill.masterSkillId] ?? '';
    return { skill: skill.name, masterSkill: masterSkillName };
  };

  const getAllTopics = (value: string | string[]): string[] => {
    if (!value || (Array.isArray(value) && value.length === 0)) return [];

    const topicIds = Array.isArray(value)
      ? value
      : value.split(',');

    return topicIds.map((topicId: string) => topicsMap[topicId.trim()] ?? '');
  };

  const contextValue: HomeworkMetadataContextValue = {
    books: data.books,
    difficulties: data.difficulties,
    masterSkills: data.masterSkills,
    skills: data.skills,
    topics: data.topics,
    dispatcher: {
      getSkillNames,
      getSkill: id => skillsMap[id],
      getDifficultyName: id => difficultiesMap[id] ?? '',
      getBookName: id => booksMap[id] ?? '',
      getTopicName: id => topicsMap[id] ?? '',
      getAllTopics,
    },
  };

  return <HomeworkMetadataContext.Provider value={contextValue}>{children}</HomeworkMetadataContext.Provider>;
};

export const useHomeworkMetadata = (): HomeworkMetadataContextValue => {
  const context = useContext(HomeworkMetadataContext);
  if (context === undefined) {
    throw new Error('useHomeworkMetadata must be used within MainHomeworkMetadataProvider');
  }
  return context;
};
